import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Box, Button, Typography, Autocomplete, TextField } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DrugAdministrationGrid from "../components/DrugAdministrationGrid";
import PrimaryMonitoringGrid from "../components/PrimaryMonitoringGrid";
import AdvancedMonitoringGrid from "../components/AdvancedMonitoringGrid";
import theme from '../../theme/theme.js';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft } from '../../utils/draftUtils';
import { API_BASE_URL } from '../../lib/constants';
import { ThemeProvider } from '@mui/material/styles';
import { useNotification } from '../../contexts/NotificationContext';

// Auto-suggestion data
const suggestions = {
  pulse: ["60-80 bpm", "80-100 bpm", "100-120 bpm", "50-70 bpm", "Regular rhythm", "Irregular rhythm"],
  bp: ["120/80 mmHg", "110/70 mmHg", "130/85 mmHg", "140/90 mmHg", "100/60 mmHg", "90/50 mmHg"],
  anaesthetist1: ["Dr. Smith", "Dr. Johnson", "Dr. Williams", "Dr. Brown", "Dr. Davis", "Dr. Miller", "Dr. Wilson", "Dr. Moore", "Dr. Taylor", "Dr. Anderson"],
  anaesthetist2: ["Dr. Thomas", "Dr. Jackson", "Dr. White", "Dr. Harris", "Dr. Martin", "Dr. Thompson", "Dr. Garcia", "Dr. Martinez", "Dr. Robinson", "Dr. Clark"],
  surgeon1: ["Dr. Rodriguez", "Dr. Lewis", "Dr. Lee", "Dr. Walker", "Dr. Hall", "Dr. Allen", "Dr. Young", "Dr. Hernandez", "Dr. King", "Dr. Wright"],
  surgeon2: ["Dr. Lopez", "Dr. Hill", "Dr. Scott", "Dr. Green", "Dr. Adams", "Dr. Baker", "Dr. Gonzalez", "Dr. Nelson", "Dr. Carter", "Dr. Mitchell"],
  inhalationalAgent: ["Sevoflurane", "Desflurane", "Isoflurane", "Propofol", "Thiopental", "Etomidate", "Ketamine"],
  oralNasal: ["Oral", "Nasal", "Oropharyngeal", "Nasopharyngeal"],
  cuffed: ["Cuffed", "Uncuffed", "High Volume Low Pressure", "Low Volume High Pressure"],
  size: ["6.0mm", "6.5mm", "7.0mm", "7.5mm", "8.0mm", "8.5mm", "9.0mm", "5.5mm", "5.0mm"],
  scopeGrade: ["Grade I", "Grade II", "Grade III", "Grade IV", "Grade IIa", "Grade IIb", "Grade III Modified"],
  n2o: ["50% N2O/50% O2", "60% N2O/40% O2", "70% N2O/30% O2", "100% O2", "Air/O2 mix"],
  oxygenF1Fe: ["FiO2 0.3", "FiO2 0.4", "FiO2 0.5", "FiO2 0.6", "FiO2 0.8", "FiO2 1.0", "FeO2 0.25", "FeO2 0.35"],
  vapourF1Fe: ["Sevo 1.0%", "Sevo 1.5%", "Sevo 2.0%", "Des 3%", "Des 4%", "Des 6%", "Iso 1%", "Iso 1.5%"],
  ecgPosition: ["Lead II", "Lead V5", "3-lead", "5-lead", "12-lead", "CM5", "Modified V5"],
  temperatureReading: ["36.0°C", "36.5°C", "37.0°C", "37.5°C", "35.5°C", "38.0°C", "Core temp", "Peripheral temp"],
  ivFluid: ["Normal Saline 100ml/hr", "Ringer's Lactate 150ml/hr", "5% Dextrose 75ml/hr", "Plasmalyte 125ml/hr", "Gelofusine 200ml/hr"],
  bloodLoss: ["<50ml", "50-100ml", "100-200ml", "200-500ml", "500-1000ml", ">1000ml", "Minimal", "Moderate"],
  urineOutput: ["0.5ml/kg/hr", "1ml/kg/hr", "1.5ml/kg/hr", "2ml/kg/hr", ">50ml/hr", "Adequate", "Oliguria"],
  tourniquet: ["60 minutes", "90 minutes", "120 minutes", "30 minutes", "45 minutes", "Not used", "Bilateral"],
  clinicalNotes: [
    "Uneventful induction and maintenance",
    "Smooth extubation, patient awake and oriented",
    "Difficult intubation - multiple attempts",
    "Hemodynamically stable throughout",
    "Blood pressure well controlled",
    "Good muscle relaxation achieved",
    "Patient positioned supine without complications",
    "Regional block supplemented general anesthesia"
  ]
};

// Enhanced AutoSuggestInput Component
const AutoSuggestInput = ({
  value,
  onChange,
  suggestions = [],
  placeholder,
  maxLength,
  type = "text",
  label,
  required = false,
  multiline = false,
  rows = 1,
  className = "",
  disabled = false
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Deduplicate suggestions
  const uniqueSuggestions = Array.from(new Set(suggestions));

  useEffect(() => {
    if (value && uniqueSuggestions.length > 0) {
      // Filter and remove exact match from suggestions
      const filtered = uniqueSuggestions
        .filter(suggestion =>
          suggestion.toLowerCase().includes(value.toLowerCase()) &&
          suggestion.toLowerCase() !== value.toLowerCase()
        )
        .slice(0, 8); // Limit to 8 suggestions for better UX
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0 && value.length > 0);
    } else {
      setShowSuggestions(false);
      setFilteredSuggestions([]);
    }
  }, [value, uniqueSuggestions]);

  const handleInputChange = (e) => {
    onChange(e);
    setHighlightedIndex(-1);
  };

  const handleSuggestionClick = (suggestion) => {
    onChange({ target: { value: suggestion } });
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
          handleSuggestionClick(filteredSuggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleFocus = () => {
    // Only show suggestions on focus if the field is empty
    if (uniqueSuggestions.length > 0 && !value) {
      setFilteredSuggestions(uniqueSuggestions.slice(0, 8));
      setShowSuggestions(true);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }, 150);
  };

  const inputElement = multiline ? (
    <textarea
      ref={inputRef}
      value={value}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      maxLength={maxLength}
      rows={rows}
      required={required}
      disabled={disabled}
      autoComplete="off"
      className={`border-2 border-gray-300 px-3 py-2 w-full rounded-lg text-sm focus:border-teal-500 focus:outline-none transition-colors resize-none ${className}`}
      aria-label={label}
    />
  ) : (
    <input
      ref={inputRef}
      type={type}
      value={value}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      maxLength={maxLength}
      required={required}
      disabled={disabled}
      autoComplete="off"
      className={`border-2 border-gray-300 px-3 py-2 w-full rounded-lg text-sm focus:border-teal-500 focus:outline-none transition-colors ${className}`}
      aria-label={label}
    />
  );

  return (
    <div className="relative">
      {inputElement}

      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-teal-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
          style={{ boxShadow: "0 4px 16px rgba(0,128,128,0.08)" }}
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              className={`px-3 py-2 cursor-pointer text-sm hover:bg-teal-100 transition-colors ${index === highlightedIndex ? 'bg-teal-200 text-teal-900 font-semibold' : 'text-gray-700'
                }`}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}

      {maxLength && (
        <div className="text-xs text-gray-500 mt-1 text-right">
          {value?.length || 0}/{maxLength}
        </div>
      )}
    </div>
  );
};

const EnhancedSelect = ({ value, onChange, options, placeholder, label, required = false, disabled = false, className = "" }) => {
  return (
    <select
      value={value || ""}
      onChange={onChange}
      required={required}
      disabled={disabled}
      autoComplete="off"
      className={`border-2 border-gray-300 px-3 py-2 w-full rounded-lg text-sm focus:border-teal-500 focus:outline-none transition-colors ${className}`}
      aria-label={label}
    >
      <option value="" disabled>
        {placeholder || "Select"}
      </option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};

// Separate SignatureCanvas component for reusability
const SignatureCanvas = React.forwardRef((props, ref) => {
  const { width = 400, height = 180 } = props;
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRefInternal = useRef(null);
  const lastPoint = useRef({ x: 0, y: 0 });

  React.useImperativeHandle(ref, () => ({
    clear: () => {
      const canvas = canvasRefInternal.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    },
    getCanvas: () => canvasRefInternal.current,
  }));

  // Mouse events
  const getPos = (e) => {
    const canvas = canvasRefInternal.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };
  const startDrawing = (e) => {
    setIsDrawing(true);
    const pos = getPos(e);
    lastPoint.current = pos;
    const ctx = canvasRefInternal.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };
  const draw = (e) => {
    if (!isDrawing) return;
    const pos = getPos(e);
    const ctx = canvasRefInternal.current.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPoint.current = pos;
  };
  const stopDrawing = () => setIsDrawing(false);

  // Touch events
  const getTouchPos = (e) => {
    const canvas = canvasRefInternal.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0] || e.changedTouches[0];
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
  };
  const startTouch = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getTouchPos(e);
    lastPoint.current = pos;
    const ctx = canvasRefInternal.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };
  const moveTouch = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getTouchPos(e);
    const ctx = canvasRefInternal.current.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPoint.current = pos;
  };
  const endTouch = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  return (
    <canvas
      ref={canvasRefInternal}
      width={width}
      height={height}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseOut={stopDrawing}
      onTouchStart={startTouch}
      onTouchMove={moveTouch}
      onTouchEnd={endTouch}
      className="border-2 border-gray-300 rounded-lg bg-white touch-none"
      aria-label="Digital signature canvas"
    />
  );
});

const AnesthesiaForm = () => {
  const { showNotification } = useNotification();
  // FIXED: Properly use useNavigate hook
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const signaturePadRef = useRef(null);

  // Chart/grid refs
  const drugGridRef = useRef();
  const primaryMonitoringRef = useRef();
  const advancedMonitoringRef = useRef();

  const [formData, setFormData] = useState({
    uhid_no: uhid_no || "",
    pulse: "",
    bp: "",
    others: "",
    signName: "",
    signDateTime: "",
    signature: "",
    anaesthetist1: "",
    anaesthetist2: "",
    surgeon1: "",
    surgeon2: "",
    oralNasal: "",
    cuffed: "",
    size: "",
    scopeGrade: "",
    inOut: "",
    ivAccess: "",
    preOxygenation: "",
    rapidSequence: "",
    inhalationalAgent: "",
    throatPack: "",
    monitoring: {
      spo2: false, ecg: false, nibp: false, etco2: false, fio2: false, temp: false, cvp: false, arterialLine: false, uop: false,
    },
    equipmentChecked: false,
    consentChecked: false,
    drugInputs: ["", "", "", "", "", "", "", ""],
    n2o: "",
    oxygenF1Fe: "",
    vapourF1Fe: "",
    ecgPosition: "",
    temperatureReading: "",
    ivFluid: "",
    bloodLoss: "",
    urineOutput: "",
    tourniquet: "",
    procedures: {
      drugReversal: false, postOperative: false, postOpWard: false, delayedRecovery: false, drugReaction: false,
      aspiration: false, cardiacArrest: false, others: false,
    },
    clinicalNotes: "",
    drugChartData: "",
    primaryMonitoringData: "",
    advancedMonitoringData: "",
  });

  // Input validation
  const validateField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case 'pulse':
        if (value && value.length > 20) {
          newErrors[field] = 'Pulse entry too long (max 10 characters)';
        } else {
          delete newErrors[field];
        }
        break;
      case 'bp':
        if (value && value.length > 20) {
          newErrors[field] = 'BP entry too long (max 20 characters)';
        } else {
          delete newErrors[field];
        }
        break;
      case 'signName':
      case 'anaesthetist1':
      case 'anaesthetist2':
      case 'surgeon1':
      case 'surgeon2':
        if (value && value.length > 100) {
          newErrors[field] = 'Name too long (max 100 characters)';
        } else {
          delete newErrors[field];
        }
        break;
      case 'clinicalNotes':
        if (value && value.length > 255) {
          newErrors[field] = 'Notes too long (max 255 characters)';
        } else {
          delete newErrors[field];
        }
        break;
      default:
        // For other string fields with max 50 characters
        if (value && value.length > 50) {
          newErrors[field] = 'Entry too long (max 50 characters)';
        } else {
          delete newErrors[field];
        }
        break;
    }

    setErrors(newErrors);
  };

  // Improved scrolling to first error
  const scrollToError = (errorList) => {
    if (Object.keys(errorList).length > 0) {
      const firstErrorField = Object.keys(errorList)[0];
      // Try finding by name or aria-label (which matches the label prop in our components)
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`) ||
        document.querySelector(`[aria-label="${firstErrorField}"]`);

      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
    }
  };

  // Enhanced change handler with validation
  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Enhanced name change handler with auto date/time and validation
  const handleNameChange = (e) => {
    const name = e.target.value;
    const now = new Date();
    const dateTimeString = now.toISOString().slice(0, 16); // Format for datetime-local input

    setFormData(prev => ({
      ...prev,
      signName: name,
      signDateTime: dateTimeString
    }));
    if (errors.signName) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.signName;
        return newErrors;
      });
    }
  };

  // Monitoring change handler
  const handleMonitoringChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      monitoring: {
        ...prev.monitoring,
        [field]: e.target.checked
      }
    }));
  };

  // Procedure change handler
  const handleProcedureChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      procedures: {
        ...prev.procedures,
        [field]: e.target.checked
      }
    }));
  };

  // Drug input change handler
  const handleDrugInputChange = (index, value) => {
    const newDrugInputs = [...formData.drugInputs];
    newDrugInputs[index] = value;
    setFormData(prev => ({
      ...prev,
      drugInputs: newDrugInputs
    }));
  };

  // Signature handlers
  const handleSignatureOpen = () => {
    setSignatureDialogOpen(true);
  };

  const handleSignatureClose = () => {
    setSignatureDialogOpen(false);
  };

  const handleSignatureClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const handleSignatureSave = () => {
    if (signaturePadRef.current) {
      const canvas = signaturePadRef.current.getCanvas();
      const signatureDataURL = canvas.toDataURL();
      setFormData(prev => ({
        ...prev,
        signature: signatureDataURL
      }));
      setSignatureDialogOpen(false);
    }
  };

  // Load existing form data including grid data
  const loadExistingData = useCallback(async () => {
    if (!uhid_no) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/get_anesthesia_form/${uhid_no}`);
      const dataArray = response.data;

      if (!dataArray || typeof dataArray !== 'object') {
        console.error("Unexpected response format:", dataArray);
        return;
      }

      const parsedData = dataArray;

      // Debug: log backend and parsed data
      console.log('Fetched backend data:', dataArray);
      console.log('Parsed data for form:', parsedData);
      // Update form state
      setFormData(prev => ({
        ...prev,
        uhid_no: parsedData.uhid_no,

        pulse: parsedData.preinduction_pulse,
        bp: parsedData.preinduction_bp,
        others: parsedData.preinduction_others,
        signName: parsedData.preinduction_sign_name,
        signDateTime: parsedData.preinduction_sign_datetime,
        signature: parsedData.signature,
        anaesthetist1: parsedData.anaesthetist1,
        anaesthetist2: parsedData.anaesthetist2,
        surgeon1: parsedData.surgeon1,
        surgeon2: parsedData.surgeon2,
        oralNasal: parsedData.oral_nasal,
        cuffed: parsedData.cuffed,
        size: parsedData.tube_size_label,
        scopeGrade: parsedData.scope_grade,
        inOut: parsedData.in_out,
        ivAccess: parsedData.induction_iv_access,
        preOxygenation: parsedData.induction_pre_oxygenation,
        rapidSequence: parsedData.induction_rapid_sequence,
        inhalationalAgent: parsedData.induction_inhalational_agent,
        throatPack: parsedData.induction_throat_pack,
        monitoring: {
          spo2: parsedData.monitoring_spo2,
          ecg: parsedData.monitoring_ecg,
          nibp: parsedData.monitoring_nibp,
          etco2: parsedData.monitoring_etco2,
          fio2: parsedData.monitoring_fio2,
          temp: parsedData.monitoring_temp,
          cvp: parsedData.monitoring_cvp,
          arterialLine: parsedData.monitoring_arterial_line,
          uop: parsedData.monitoring_uop,
        },
        equipmentChecked: parsedData.equipment_checked,
        consentChecked: parsedData.consent_checked,
        drugInputs: typeof parsedData.drug_inputs === 'string' ? JSON.parse(parsedData.drug_inputs) : (Array.isArray(parsedData.drug_inputs) ? parsedData.drug_inputs : ["", "", "", "", "", "", "", ""]),
        n2o: parsedData.gas_n2o,
        oxygenF1Fe: parsedData.gas_oxygen_f1fe,
        vapourF1Fe: parsedData.gas_vapour_f1fe,
        ecgPosition: parsedData.ecg_position,
        temperatureReading: parsedData.temperature_reading,
        ivFluid: parsedData.fm_iv_fluid,
        bloodLoss: parsedData.fm_blood_loss,
        urineOutput: parsedData.fm_urine_output,
        tourniquet: parsedData.fm_tourniquet,
        procedures: {
          drugReversal: parsedData.proc_drug_reversal,
          postOperative: parsedData.proc_post_operative,
          postOpWard: parsedData.proc_post_op_ward,
          delayedRecovery: parsedData.proc_delayed_recovery,
          drugReaction: parsedData.proc_drug_reaction,
          aspiration: parsedData.proc_aspiration,
          cardiacArrest: parsedData.proc_cardiac_arrest,
          others: parsedData.proc_others,
        },
        clinicalNotes: parsedData.clinical_notes,
        drugChartData: typeof parsedData.drug_chart_data === 'string' ? JSON.parse(parsedData.drug_chart_data) : (parsedData.drug_chart_data || []),
        primaryMonitoringData: typeof parsedData.primary_monitoring_data === 'string' ? JSON.parse(parsedData.primary_monitoring_data) : (parsedData.primary_monitoring_data || []),
        advancedMonitoringData: typeof parsedData.advanced_monitoring_data === 'string' ? JSON.parse(parsedData.advanced_monitoring_data) : (parsedData.advanced_monitoring_data || []),
      }));

      setSuccess(true);

      // Load grid data (after DOM ready)
      setTimeout(() => {
        try {
          if (parsedData.drug_chart_data) {
            const drugData = JSON.parse(parsedData.drug_chart_data);
            drugGridRef.current?.setGridData(drugData);
          }
        } catch (e) {
          console.error("Error parsing drug chart data:", e);
        }

        try {
          if (parsedData.primary_monitoring_data) {
            const primaryData = JSON.parse(parsedData.primary_monitoring_data);
            primaryMonitoringRef.current?.setGridData(primaryData);
          }
        } catch (e) {
          console.error("Error parsing primary monitoring data:", e);
        }

        try {
          if (parsedData.advanced_monitoring_data) {
            const advancedData = JSON.parse(parsedData.advanced_monitoring_data);
            advancedMonitoringRef.current?.setGridData(advancedData);
          }
        } catch (e) {
          console.error("Error parsing advanced monitoring data:", e);
        }
      }, 200);

    } catch (error) {
      console.warn("No existing data found or error occurred:", error);
    } finally {
      setLoading(false);
    }
  }, [uhid_no]);

  // Load draft if any
  useEffect(() => {
    const draft = loadDraft(DRAFT_KEYS.PRE_ANAESTHETIC_FORM, uhid_no);
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
    }
  }, [uhid_no]);

  useEffect(() => {
    loadExistingData();
  }, [uhid_no]);

  // Save chart/grid data before submit
  const collectGridData = () => {
    return {
      drugChartData: JSON.stringify(drugGridRef.current?.getGridData() || []),
      primaryMonitoringData: JSON.stringify(primaryMonitoringRef.current?.getGridData() || []),
      advancedMonitoringData: JSON.stringify(advancedMonitoringRef.current?.getGridData() || []),
    };
  };

  const prepareDataForBackend = () => {
    const gridData = collectGridData();
    // Format signDateTime to 'YYYY-MM-DD HH:mm:SS' if present
    let formattedSignDateTime = formData.signDateTime;
    if (formattedSignDateTime && formattedSignDateTime.includes('T')) {
      const [date, time] = formattedSignDateTime.split('T');
      formattedSignDateTime = `${date} ${time.length === 5 ? time + ':00' : time}`;
    }
    console.log("Collected Grid Data:", gridData);
    return {
      uhid_no: uhid_no,

      preinduction_pulse: formData.pulse,
      preinduction_bp: formData.bp,
      preinduction_others: formData.others,
      preinduction_sign_name: formData.signName,
      preinduction_sign_datetime: formattedSignDateTime,
      signature: formData.signature,
      anaesthetist1: formData.anaesthetist1,
      anaesthetist2: formData.anaesthetist2,
      surgeon1: formData.surgeon1,
      surgeon2: formData.surgeon2,
      oral_nasal: formData.oralNasal,
      cuffed: formData.cuffed,
      tube_size_label: formData.size,
      scope_grade: formData.scopeGrade,
      in_out: formData.inOut,
      induction_iv_access: formData.ivAccess,
      induction_pre_oxygenation: formData.preOxygenation,
      induction_rapid_sequence: formData.rapidSequence,
      induction_inhalational_agent: formData.inhalationalAgent,
      induction_throat_pack: formData.throatPack,
      monitoring_spo2: formData.monitoring.spo2,
      monitoring_ecg: formData.monitoring.ecg,
      monitoring_nibp: formData.monitoring.nibp,
      monitoring_etco2: formData.monitoring.etco2,
      monitoring_fio2: formData.monitoring.fio2,
      monitoring_temp: formData.monitoring.temp,
      monitoring_cvp: formData.monitoring.cvp,
      monitoring_arterial_line: formData.monitoring.arterialLine,
      monitoring_uop: formData.monitoring.uop,
      equipment_checked: formData.equipmentChecked,
      consent_checked: formData.consentChecked,
      drug_inputs: JSON.stringify(formData.drugInputs),
      gas_n2o: formData.n2o,
      gas_oxygen_f1fe: formData.oxygenF1Fe,
      gas_vapour_f1fe: formData.vapourF1Fe,
      ecg_position: formData.ecgPosition,
      temperature_reading: formData.temperatureReading,
      fm_iv_fluid: formData.ivFluid,
      fm_blood_loss: formData.bloodLoss,
      fm_urine_output: formData.urineOutput,
      fm_tourniquet: formData.tourniquet,
      proc_drug_reversal: formData.procedures.drugReversal,
      proc_post_operative: formData.procedures.postOperative,
      proc_post_op_ward: formData.procedures.postOpWard,
      proc_delayed_recovery: formData.procedures.delayedRecovery,
      proc_drug_reaction: formData.procedures.drugReaction,
      proc_aspiration: formData.procedures.aspiration,
      proc_cardiac_arrest: formData.procedures.cardiacArrest,
      proc_others: formData.procedures.others,
      clinical_notes: formData.clinicalNotes,
      drug_chart_data: gridData.drugChartData,
      primary_monitoring_data: gridData.primaryMonitoringData,
      advanced_monitoring_data: gridData.advancedMonitoringData,
    };
  };

  // Form validation before submit
  const validateForm = () => {
    const newErrors = {};

    // Check for validation errors
    if (Object.keys(errors).length > 0) {
      return false;
    }

    // Required field validation
    if (!formData.signName.trim()) {
      newErrors.signName = 'Physician name is required';
    }

    if (!formData.signDateTime) {
      newErrors.signDateTime = 'Date and time is required';
    }

    if (!formData.anaesthetist1.trim()) {
      newErrors.anaesthetist1 = 'Primary anesthetist is required';
    }

    if (!formData.surgeon1.trim()) {
      newErrors.surgeon1 = 'Primary surgeon is required';
    }

    if (!formData.oralNasal) {
      newErrors.oralNasal = 'Route is required';
    }

    if (!formData.cuffed) {
      newErrors.cuffed = 'Cuff type is required';
    }

    if (!formData.size) {
      newErrors.size = 'Tube size is required';
    }

    if (!formData.scopeGrade) {
      newErrors.scopeGrade = 'Laryngoscope grade is required';
    }

    if (!formData.inhalationalAgent.trim()) {
      newErrors.inhalationalAgent = 'Inhalational agent is required';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      scrollToError(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };
  const handleBack = () => {
    navigate(`/mainPage/${uhid_no}`, {
      state: { from: 'PreOperativePhase', status: 'success', step: 1 },
    });
  };
  const goToVideoRecorder = (formName, uhid_no) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };
  // FIXED: Complete rewrite of handleSubmit with proper navigation
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prevent multiple submissions
    if (loading) return;

    // Validate form
    if (!validateForm()) {
      showNotification('error', 'Please correct the errors in the form before submitting.');
      return;
    }

    setLoading(true);
    try {
      const backendData = prepareDataForBackend();
      console.log("Submitting data:", backendData);
      if (success) {
        await axios.put(`${API_BASE_URL}/update_anesthesia_form/${uhid_no}`, backendData);
        showNotification('success', "Pre Induction Assessment Form updated successfully!");
      } else {
        await axios.post(`${API_BASE_URL}/anesthesia_form`, backendData);
        setSuccess(true);
        showNotification('success', 'Pre Induction Assessment Form submitted successfully!');
      }

      // Clear draft after successful submission
      clearDraft(DRAFT_KEYS.PRE_ANAESTHETIC_FORM, uhid_no);
      navigate(`/mainPage/${formData.uhid_no}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    } catch (error) {
      console.error('Error submitting the form!', error);
      showNotification('error', 'An error occurred while submitting the form. Please try again.');
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="max-w-7xl mx-auto p-2 md:p-4 bg-gradient-to-br from-teal-50 to-white min-h-screen">
        <div className="bg-white shadow-xl rounded-xl p-3 md:p-6 border border-gray-200">
          <div className="text-center mb-6">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{ marginLeft: 2 }}
              >
                Back
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img src="/logo.png" alt="Logo" className="w-8 h-8" />
                <Typography variant="h6" sx={{ color: '#00695c' }}>OTMS</Typography>
              </Box>
            </Box>

            <h1 className="text-3xl md:text-4xl font-bold text-teal-800 mb-2">
              PRE INDUCTION ASSESSMENT
            </h1>
            <div className="w-24 h-1 bg-teal-600 mx-auto rounded-full"></div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="border-2 border-teal-200 rounded-xl p-4 bg-gradient-to-br from-teal-50 to-white shadow-sm">
                <h3 className="text-lg font-bold mb-4 text-teal-800 flex items-center">
                  <div className="w-2 h-6 bg-teal-600 rounded-full mr-3"></div>
                  Pre Induction Assessment
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">Pulse & Blood Pressure</label>
                    <AutoSuggestInput
                      value={formData.pulse}
                      onChange={handleChange("pulse")}
                      suggestions={suggestions.pulse}
                      placeholder="e.g., 72 bpm, 120/80 mmHg"
                      maxLength={20}
                      label="Pulse and Blood Pressure"
                    />
                    {errors.pulse && <p className="text-red-500 text-xs mt-1">{errors.pulse}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">Blood Pressure</label>
                    <AutoSuggestInput
                      value={formData.bp}
                      onChange={handleChange("bp")}
                      suggestions={suggestions.bp}
                      placeholder="e.g., 120/80 mmHg"
                      maxLength={20}
                      label="Blood Pressure"
                    />
                    {errors.bp && <p className="text-red-500 text-xs mt-1">{errors.bp}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">Other Observations</label>
                    <AutoSuggestInput
                      value={formData.others}
                      onChange={handleChange("others")}
                      suggestions={[]}
                      placeholder="Any additional pre-operative notes..."
                      multiline={true}
                      rows={3}
                      label="Other Observations"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">Physician Name <span style={{ color: 'red' }}>*</span></label>
                    <AutoSuggestInput
                      value={formData.signName}
                      onChange={handleNameChange}
                      suggestions={suggestions.anaesthetist1}
                      placeholder="Dr. Full Name"
                      maxLength={100}
                      required={true}
                      label="Physician Name"
                    />
                    {errors.signName && <p className="text-red-500 text-xs mt-1">{errors.signName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">Date & Time <span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="datetime-local"
                      value={formData.signDateTime}
                      onChange={handleChange("signDateTime")}
                      required
                      autoComplete="off"
                      className="border-2 border-gray-300 px-3 py-2 w-full rounded-lg text-sm focus:border-teal-500 focus:outline-none transition-colors"
                      aria-label="Date and Time"
                    />
                    {errors.signDateTime && <p className="text-red-500 text-xs mt-1">{errors.signDateTime}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={handleSignatureOpen}
                    className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-4 py-3 rounded-lg text-sm w-full font-semibold shadow-md transition-all duration-200 transform hover:scale-105"
                    aria-label="Add Digital Signature"
                  >
                    Digital Signature
                  </button>
                  {formData.signature && (
                    <div className="mt-3 p-2 border-2 border-teal-200 rounded-lg bg-teal-50">
                      <p className="text-xs text-teal-700 font-medium mb-2">Signature Captured ✓</p>
                      <img
                        src={formData.signature}
                        alt="Digital Signature"
                        className="border border-gray-300 w-full h-16 object-contain bg-white rounded"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="border-2 border-teal-200 rounded-xl p-4 bg-gradient-to-br from-teal-50 to-white shadow-sm">
                <h3 className="text-lg font-bold mb-4 text-teal-800 flex items-center">
                  <div className="w-2 h-6 bg-teal-600 rounded-full mr-3"></div>
                  Medical Staff
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">Primary Anesthetist <span style={{ color: 'red' }}>*</span></label>
                    <AutoSuggestInput
                      value={formData.anaesthetist1}
                      onChange={handleChange("anaesthetist1")}
                      suggestions={suggestions.anaesthetist1}
                      placeholder="Dr. Primary Anesthetist Name"
                      maxLength={100}
                      label="Primary Anesthetist"
                      required={true}
                    />
                    {errors.anaesthetist1 && <p className="text-red-500 text-xs mt-1">{errors.anaesthetist1}</p>}
                    <p className="text-xs text-gray-500 mt-1">Enter the primary anesthetist's name</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">Assistant Anesthetist</label>
                    <AutoSuggestInput
                      value={formData.anaesthetist2}
                      onChange={handleChange("anaesthetist2")}
                      suggestions={suggestions.anaesthetist2}
                      placeholder="Dr. Assistant Anesthetist Name"
                      maxLength={100}
                      label="Assistant Anesthetist"
                    />
                    {errors.anaesthetist2 && <p className="text-red-500 text-xs mt-1">{errors.anaesthetist2}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">Primary Surgeon <span style={{ color: 'red' }}>*</span></label>
                    <AutoSuggestInput
                      value={formData.surgeon1}
                      onChange={handleChange("surgeon1")}
                      suggestions={suggestions.surgeon1}
                      placeholder="Dr. Primary Surgeon Name"
                      maxLength={100}
                      label="Primary Surgeon"
                      required={true}
                    />
                    {errors.surgeon1 && <p className="text-red-500 text-xs mt-1">{errors.surgeon1}</p>}
                    <p className="text-xs text-gray-500 mt-1">Enter the primary surgeon's name</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">Assistant Surgeon</label>
                    <AutoSuggestInput
                      value={formData.surgeon2}
                      onChange={handleChange("surgeon2")}
                      suggestions={suggestions.surgeon2}
                      placeholder="Dr. Assistant Surgeon Name"
                      maxLength={100}
                      label="Assistant Surgeon"
                    />
                    {errors.surgeon2 && <p className="text-red-500 text-xs mt-1">{errors.surgeon2}</p>}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="border-2 border-teal-200 rounded-xl p-4 bg-gradient-to-br from-teal-50 to-white shadow-sm">
                  <h3 className="text-lg font-bold mb-4 text-teal-800 flex items-center">
                    <div className="w-2 h-6 bg-teal-600 rounded-full mr-3"></div>
                    Intubation & Induction Details
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-gray-700">Route</label>
                      <EnhancedSelect
                        value={formData.oralNasal}
                        onChange={handleChange("oralNasal")}
                        options={suggestions.oralNasal}
                        placeholder="Select Route"
                        label="Route"
                        required={true}
                      />
                      <p className="text-xs text-gray-500 mt-1">Select the intubation route (oral or nasal)</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-gray-700">Cuff Type</label>
                      <EnhancedSelect
                        value={formData.cuffed}
                        onChange={handleChange("cuffed")}
                        options={suggestions.cuffed}
                        placeholder="Select Type"
                        label="Cuff Type"
                        required={true}
                      />
                      <p className="text-xs text-gray-500 mt-1">Select the type of endotracheal tube cuff</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-gray-700">Tube Size *</label>
                      <EnhancedSelect
                        value={formData.size}
                        onChange={handleChange("size")}
                        options={suggestions.size}
                        placeholder="Select Size"
                        label="Tube Size"
                        required={true}
                      />
                      <p className="text-xs text-gray-500 mt-1">Select the size of the endotracheal tube</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-gray-700">Laryngoscope *</label>
                      <EnhancedSelect
                        value={formData.scopeGrade}
                        onChange={handleChange("scopeGrade")}
                        options={suggestions.scopeGrade}
                        placeholder="Select Grade"
                        label="Laryngoscope Grade"
                        required={true}
                        className={errors.scopeGrade ? 'border-red-500' : ''}
                      />
                      {errors.scopeGrade && <p className="text-red-500 text-[10px] mt-0.5">{errors.scopeGrade}</p>}
                      <p className="text-xs text-gray-500 mt-1">Select the Cormack-Lehane grade for laryngoscopy</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-gray-700">I.V Access</label>
                      <EnhancedSelect
                        value={formData.ivAccess}
                        onChange={handleChange("ivAccess")}
                        options={["Yes", "No"]}
                        placeholder="Select"
                        label="I.V Access"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-gray-700">Pre-Oxygenation</label>
                      <EnhancedSelect
                        value={formData.preOxygenation}
                        onChange={handleChange("preOxygenation")}
                        options={["Yes", "No"]}
                        placeholder="Select"
                        label="Pre-Oxygenation"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-gray-700">Rapid Sequence Induction</label>
                      <EnhancedSelect
                        value={formData.rapidSequence}
                        onChange={handleChange("rapidSequence")}
                        options={["Yes", "No"]}
                        placeholder="Select"
                        label="Rapid Sequence Induction"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-gray-700">Inhalational Agent *</label>
                      <AutoSuggestInput
                        value={formData.inhalationalAgent}
                        onChange={handleChange("inhalationalAgent")}
                        suggestions={suggestions.inhalationalAgent}
                        placeholder="e.g. Sevoflurane"
                        maxLength={100}
                        label="Inhalational Agent"
                        className={`text-xs ${errors.inhalationalAgent ? 'border-red-500' : ''}`}
                        required={true}
                      />
                      {errors.inhalationalAgent && <p className="text-red-500 text-[10px] mt-0.5">{errors.inhalationalAgent}</p>}
                      <p className="text-xs text-gray-500 mt-1">Select the primary inhalational agent used</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1 text-gray-700">Throat Pack Used</label>
                      <EnhancedSelect
                        value={formData.throatPack}
                        onChange={handleChange("throatPack")}
                        options={["Yes", "No"]}
                        placeholder="Select"
                        label="Throat Pack Used"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-semibold mb-1 text-gray-700">Tube Position</label>
                      <AutoSuggestInput
                        value={formData.inOut}
                        onChange={handleChange("inOut")}
                        suggestions={["22cm at teeth", "20cm at teeth", "24cm at teeth", "21cm at lips", "23cm at lips"]}
                        placeholder="Depth at teeth/lips"
                        maxLength={50}
                        label="Tube Position"
                        className="text-xs"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-2 border-teal-200 rounded-xl p-4 bg-gradient-to-br from-teal-50 to-white shadow-sm">
                  <h3 className="text-lg font-bold mb-4 text-teal-800 flex items-center">
                    <div className="w-2 h-6 bg-teal-600 rounded-full mr-3"></div>
                    Monitoring Setup
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm mb-4">
                    {Object.entries({
                      spo2: "SpO₂",
                      ecg: "ECG",
                      nibp: "NIBP",
                      etco2: "ETCO₂",
                      fio2: "FiO₂",
                      temp: "Temperature",
                      cvp: "CVP",
                      arterialLine: "Arterial Line",
                      uop: "Urine Output",
                    }).map(([key, label]) => (
                      <label
                        key={key}
                        className="flex items-center p-2 rounded-lg hover:bg-teal-100 transition-colors cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.monitoring[key]}
                          onChange={handleMonitoringChange(key)}
                          autoComplete="off"
                          className="mr-2 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                          aria-label={label}
                        />
                        <span className="font-medium">{label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="pt-3 border-t border-teal-200 space-y-2">
                    <label className="flex items-center text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={formData.equipmentChecked}
                        onChange={(e) => setFormData({ ...formData, equipmentChecked: e.target.checked })}
                        autoComplete="off"
                        className="mr-3 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                        aria-label="Equipment Safety Check"
                      />
                      Equipment Safety Check Complete
                    </label>
                    <label className="flex items-center text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={formData.consentChecked}
                        onChange={(e) => setFormData({ ...formData, consentChecked: e.target.checked })}
                        autoComplete="off"
                        className="mr-3 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                        aria-label="Patient Consent Verified"
                      />
                      Patient Consent Verified
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-8 border-gray-300" />

            <div className="space-y-8">
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-3 h-8 bg-teal-600 rounded-full mr-4"></div>
                  <h2 className="text-2xl font-bold text-teal-700">Drug Administration Timeline</h2>
                </div>
                <DrugAdministrationGrid
                  ref={drugGridRef}
                  drugInputs={formData.drugInputs}
                  onDrugInputChange={handleDrugInputChange}
                  rows={8}
                  cols={30}
                />
              </div>

              <section className="mt-8">
                <div className="flex items-center mb-6">
                  <div className="w-3 h-8 bg-teal-600 rounded-full mr-4"></div>
                  <h2 className="text-2xl font-bold text-teal-700">Primary Vital Signs Monitoring</h2>
                </div>
                <PrimaryMonitoringGrid ref={primaryMonitoringRef} />
              </section>

              <section className="mt-8">
                <div className="flex items-center mb-6">
                  <div className="w-3 h-8 bg-teal-600 rounded-full mr-4"></div>
                  <h2 className="text-2xl font-bold text-teal-700">Advanced Monitoring Dashboard</h2>
                </div>
                <AdvancedMonitoringGrid ref={advancedMonitoringRef} />
              </section>

              <div>
                <div className="flex items-center mb-6">
                  <div className="w-3 h-8 bg-teal-600 rounded-full mr-4"></div>
                  <h2 className="text-2xl font-bold text-teal-700">Clinical Documentation & Notes</h2>
                </div>
                <div className="border-2 border-teal-200 rounded-xl p-6 bg-gradient-to-br from-teal-50 to-white shadow-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <h4 className="text-lg font-semibold mb-4 text-gray-700 flex items-center">Gas Concentrations</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600">N₂O/O₂/Air Mix</label>
                          <AutoSuggestInput
                            value={formData.n2o}
                            onChange={handleChange("n2o")}
                            suggestions={suggestions.n2o}
                            placeholder="e.g., 50/50/0%"
                            maxLength={50}
                            label="N₂O/O₂/Air Mix"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600">Oxygen FiO₂/FeO₂</label>
                          <AutoSuggestInput
                            value={formData.oxygenF1Fe}
                            onChange={handleChange("oxygenF1Fe")}
                            suggestions={suggestions.oxygenF1Fe}
                            placeholder="e.g., 0.5/0.45"
                            maxLength={50}
                            label="Oxygen FiO₂/FeO₂"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600">Volatile Agent</label>
                          <AutoSuggestInput
                            value={formData.vapourF1Fe}
                            onChange={handleChange("vapourF1Fe")}
                            suggestions={suggestions.vapourF1Fe}
                            placeholder="e.g., Sevo 1.5%"
                            maxLength={50}
                            label="Volatile Agent"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <h4 className="text-lg font-semibold mb-4 text-gray-700 flex items-center">Additional Monitoring</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600">ECG Lead Position</label>
                          <AutoSuggestInput
                            value={formData.ecgPosition}
                            onChange={handleChange("ecgPosition")}
                            suggestions={suggestions.ecgPosition}
                            placeholder="Lead II, V5"
                            maxLength={50}
                            label="ECG Lead Position"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600">Core Temperature</label>
                          <AutoSuggestInput
                            value={formData.temperatureReading}
                            onChange={handleChange("temperatureReading")}
                            suggestions={suggestions.temperatureReading}
                            placeholder="e.g., 36.8°C"
                            maxLength={50}
                            label="Core Temperature"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <h4 className="text-lg font-semibold mb-4 text-gray-700 flex items-center">Fluid Management</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600">IV Fluids</label>
                          <AutoSuggestInput
                            value={formData.ivFluid}
                            onChange={handleChange("ivFluid")}
                            suggestions={suggestions.ivFluid}
                            placeholder="Type & Rate (ml/hr)"
                            maxLength={50}
                            label="IV Fluids"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600">Estimated Blood Loss</label>
                          <AutoSuggestInput
                            value={formData.bloodLoss}
                            onChange={handleChange("bloodLoss")}
                            suggestions={suggestions.bloodLoss}
                            placeholder="ml"
                            maxLength={50}
                            label="Estimated Blood Loss"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600">Urine Output</label>
                          <AutoSuggestInput
                            value={formData.urineOutput}
                            onChange={handleChange("urineOutput")}
                            suggestions={suggestions.urineOutput}
                            placeholder="ml/hr"
                            maxLength={50}
                            label="Urine Output"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600">Tourniquet Time</label>
                          <AutoSuggestInput
                            value={formData.tourniquet}
                            onChange={handleChange("tourniquet")}
                            suggestions={suggestions.tourniquet}
                            placeholder="Duration (min)"
                            maxLength={50}
                            label="Tourniquet Time"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-teal-200">
                    <h4 className="text-lg font-semibold mb-4 text-gray-700 flex items-center">
                      Additional Procedures & Complications
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {Object.entries({
                        drugReversal: "Drug Reversal",
                        postOperative: "Post Operative Care",
                        postOpWard: "Post Op Ward Transfer",
                        delayedRecovery: "Delayed Recovery",
                        drugReaction: "Drug Reaction",
                        aspiration: "Aspiration Event",
                        cardiacArrest: "Cardiac Event",
                        others: "Other Complications",
                      }).map(([key, label]) => (
                        <label
                          key={key}
                          className="flex items-center p-3 rounded-lg bg-white border border-gray-200 hover:bg-teal-50 transition-colors cursor-pointer shadow-sm"
                        >
                          <input
                            type="checkbox"
                            checked={formData.procedures[key]}
                            onChange={handleProcedureChange(key)}
                            autoComplete="off"
                            className="mr-3 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                            aria-label={label}
                          />
                          <span className="text-sm font-medium">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-teal-200">
                    <h4 className="text-lg font-semibold mb-3 text-gray-700">Clinical Notes</h4>
                    <AutoSuggestInput
                      value={formData.clinicalNotes}
                      onChange={handleChange("clinicalNotes")}
                      suggestions={suggestions.clinicalNotes}
                      placeholder="Additional clinical observations, complications, or important notes during the procedure..."
                      multiline={true}
                      rows={4}
                      maxLength={255}
                      label="Clinical Notes"
                    />
                    {errors.clinicalNotes && <p className="text-red-500 text-xs mt-1">{errors.clinicalNotes}</p>}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t-2 border-gray-200 text-center">
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                <DraftSaveButton
                  formKey={DRAFT_KEYS.PRE_ANAESTHETIC_FORM}
                  uhidNo={uhid_no}
                  formData={formData}
                  onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                  variant="outlined"
                  isUpdate={success}
                  size="large"
                  disabled={loading}

                />
                <button
                  type="submit"
                  disabled={loading || Object.keys(errors).length > 0}
                  className={`font-bold py-4 px-12 rounded-xl shadow-xl transition-all duration-300 text-lg ${loading || Object.keys(errors).length > 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-teal-600 to-teal-600 hover:from-teal-700 hover:to-teal-700 transform hover:scale-105 hover:shadow-2xl'
                    } text-white`}
                  aria-label="Submit Anesthesia Record"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {success ? "Updating..." : "Submitting..."}
                    </div>
                  ) : (
                    success ? "Update Complete Anesthesia Record" : "Submit Complete Anesthesia Record"
                  )}
                </button>
              </div>

              {Object.keys(errors).length > 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 font-medium text-sm">
                    Please correct the following errors before submitting:
                  </p>
                  <ul className="text-red-600 text-sm mt-1 list-disc list-inside">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-gradient-to-r from-teal-50 to-teal-50 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Record Completion Checklist</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center justify-center p-3 bg-white rounded-lg border">
                    <span className={`mr-2 ${formData.signature ? "text-teal-500" : "text-gray-400"}`}>
                      {formData.signature ? "✅" : "⭕"}
                    </span>
                    Digital Signature
                  </div>
                  <div className="flex items-center justify-center p-3 bg-white rounded-lg border">
                    <span className={`mr-2 ${formData.equipmentChecked ? "text-teal-500" : "text-gray-400"}`}>
                      {formData.equipmentChecked ? "✅" : "⭕"}
                    </span>
                    Equipment Check
                  </div>
                  <div className="flex items-center justify-center p-3 bg-white rounded-lg border">
                    <span className={`mr-2 ${formData.consentChecked ? "text-teal-500" : "text-gray-400"}`}>
                      {formData.consentChecked ? "✅" : "⭕"}
                    </span>
                    Consent Verified
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mt-3">
                All data will be securely stored and made available for medical record review
              </p>
            </div>
          </form>

          {signatureDialogOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl">
                <div className="text-center mb-4">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Digital Signature</h3>
                  <p className="text-gray-600">Please sign below to authenticate this record</p>
                </div>
                <div className="mb-6 flex justify-center">
                  <SignatureCanvas ref={signaturePadRef} width={400} height={180} />
                </div>
                <div className="flex flex-wrap gap-3 justify-center">
                  <button
                    type="button"
                    onClick={handleSignatureClear}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors shadow-md"
                    aria-label="Clear Signature"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={handleSignatureSave}
                    className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors shadow-md"
                    aria-label="Save Signature"
                  >
                    Save Signature
                  </button>
                  <button
                    type="button"
                    onClick={handleSignatureClose}
                    className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors shadow-md"
                    aria-label="Cancel Signature"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default AnesthesiaForm;